import React, { useState, useEffect, useMemo } from "react";
import ContentBlock from "../../components/Layout/ContentBlock";
import Container from "../../components/Layout/Container";
import {
  IconDocNumber,
  IconNo,
  IconTrash,
  IconWarehouse,
  IconWarehouse2,
} from "../../components/Layout/Icons";
import { Modal, Alert, Checkbox, Spin } from "antd";
import ButtonSecondary from "../../components/common/ButtonSecondary";
import TableUi from "../../components/Layout/TableUI";
import FilterMenu from "../../components/Layout/FilterMenu";
import { MdDateRange } from "react-icons/md";
import PaginationComp from "../../components/Dashboard/items/Pagination";
import {
  antdMessageError,
  antdMessageSuccess,
  paginate,
} from "../../helpers/Utils";
import AntdConfirm from "../../helpers/AntdConfirm";
import { QueryClient, QueryClientProvider, useMutation } from "react-query";
import { useFetch } from "../../hooks";
import Axios from "axios";
import CostPricePopover from "../../components/Products/Items/CostPricePopover";
import { Tooltip } from "antd";
import { createRef } from "react";
import RegularOrderChildren from "../../components/common/GlobalModals/Orders/RegularOrderChildren";
import { getOrder } from "../../redux/Actions/orderAction";
import { getStores } from "../../redux/Actions/rivileAction";
import { useDispatch, useSelector } from "react-redux";
import { createConsumer } from "@rails/actioncable";
import VvDepartmentPopover from "../../components/Products/Items/VvDepartmentPopover";

const { confirm } = Modal;
export const queryDocumentsClient = new QueryClient();

function DocumentDetailsPage({ match, history }) {
  return (
    <QueryClientProvider client={queryDocumentsClient}>
      <DocumentDetailsScreen match={match} history={history} />
    </QueryClientProvider>
  );
}

export default DocumentDetailsPage;

function DocumentDetailsScreen({ match, history }) {
  const dispatch = useDispatch();
  const modalRef = createRef();
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(50);
  const [order_id, set_order_id] = useState(null);
  const [checkedItems, setCheckedItems] = useState([]);
  const [generatingInvoices, setGeneratingInvoices] = useState(false);
  const user_id = useSelector((state) => state.auth.user?.id);

  const documentId = new URLSearchParams(window.location.search).get('id');

  const {
    value: documentData,
    loading: loadingDocument,
    setReFetch: setReFetchDocument,
  } = useFetch(documentId ? `/api/v1/tr_inner_documents/${documentId}?page=${page}&per_page=${pageCount}` : null);

  const {
    value: valueStores,
    loading: loadingStores,
  } = useFetch("/api/v1/also_incomes/vv_stores");

  const documentItems = documentData?.data?.tr_request_items || [];

  const total_items = useMemo(() => {
    return documentData?.total || documentItems?.length || 0;
  }, [documentData?.total, documentItems?.length]);

  const filteredData = useMemo(() => {
    return documentItems || [];
  }, [documentItems]);

  useEffect(() => {
    if (documentId) {
      setReFetchDocument(`/api/v1/tr_inner_documents/${documentId}?page=${page}&per_page=${pageCount}`);
      setCheckedItems([]);
    }
  }, [page, pageCount, documentId]);

  // TrClientInvoiceJob broadcasts once it finished generating the invoices
  useEffect(() => {
    if (!user_id) return;

    const consumer = createConsumer();
    const subscription = consumer.subscriptions.create(
      { channel: "ExportedDocumentChannel", user_id: user_id },
      {
        received(data) {
          if (data?.exported_document?.element !== "tr_client_invoice_progress") return;
          setGeneratingInvoices(false);
          setReFetchDocument(`/api/v1/tr_inner_documents/${documentId}?page=${page}&per_page=${pageCount}`);
        },
      }
    );

    return () => subscription.unsubscribe();
  }, [user_id, documentId, page, pageCount]);

  // FakeOrder items have no client to invoice
  const isInvoiceable = (item) =>
    !!item.order_row_id &&
    !item.order_number?.startsWith("FO") &&
    !item.client_invoice_numbers?.length;

  const selectableIds = useMemo(() => {
    return filteredData.filter(isInvoiceable).map((item) => item.id);
  }, [filteredData]);

  const canCreateClientInvoices = ["in_progress", "completed"].includes(documentData?.data?.status);

  const handleCheckboxChange = (id) => {
    setCheckedItems((prev) =>
      prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]
    );
  };

  const handleCheckAll = (checked) => {
    setCheckedItems(checked ? selectableIds : []);
  };

  const selectAllTh = canCreateClientInvoices && selectableIds.length > 0 ? (
    <Checkbox
      checked={checkedItems.length > 0 && checkedItems.length === selectableIds.length}
      indeterminate={checkedItems.length > 0 && checkedItems.length < selectableIds.length}
      onChange={(e) => handleCheckAll(e.target.checked)}
    />
  ) : "";

  const mutationCancel = useMutation({
    mutationFn: (id) => {
      return Axios.post(`/api/v1/tr_inner_documents/${id}/set_canceled`);
    },
    onSuccess: (data, id) => {
      antdMessageSuccess("Document canceled successfully");
      window.location.reload();
    },
    onError: (error) => {
      antdMessageError(error?.response?.data?.message || "Failed to cancel document");
    },
  });

  const mutationInner = useMutation({
    mutationFn: (id) => {
      return Axios.post(`/api/v1/tr_inner_documents/${id}/create_inner`);
    },
    onSuccess: (data, id) => {
      antdMessageSuccess("Internal document created successfully");
      window.location.reload();
    },
    onError: (error) => {
      antdMessageError(error?.response?.data?.message || "Failed to create internal document");
    },
  });

  const mutationInvoice = useMutation({
    mutationFn: (id) => {
      return Axios.post(`/api/v1/tr_inner_documents/${id}/create_invoice`);
    },
    onSuccess: (data, id) => {
      antdMessageSuccess("Invoice created successfully");
      window.location.reload();
    },
    onError: (error) => {
      antdMessageError(error?.response?.data?.message || "Failed to create invoice");
    },
  });

  const mutationCost = useMutation({
    mutationFn: (id) => {
      return Axios.post(`/api/v1/tr_inner_documents/${id}/update_cost`);
    },
    onSuccess: (data, id) => {
      antdMessageSuccess("Prime cost updated successfully");
      window.location.reload();
    },
    onError: (error) => {
      antdMessageError(error?.response?.data?.message || "Failed to update cost price");
    },
  });

  const mutationClientInvoices = useMutation({
    mutationFn: (itemIds) => {
      return Axios.post(`/api/v1/tr_inner_documents/${documentId}/create_client_invoices`, {
        tr_request_item_ids: itemIds,
      });
    },
    onSuccess: ({ data }) => {
      if (data?.success) {
        antdMessageSuccess(data?.message || "Client invoices are being generated in the background");
        setCheckedItems([]);
        setGeneratingInvoices(true);
      } else {
        antdMessageError(data?.message || "Failed to create client invoices");
      }
    },
    onError: (error) => {
      setGeneratingInvoices(false);
      antdMessageError(error?.response?.data?.error || error?.response?.data?.message || "Failed to create client invoices");
    },
  });

  const updateCostPriceMutation = useMutation({
    mutationFn: ({ itemId, prime_cost, total_cost, price_index }) => {
      return Axios.put(`/api/v1/tr_request_items/${itemId}`, {
        tr_request_item: {
          prime_cost,
          total_cost,
          price_index
        },
      });
    },
    onSuccess: () => {
      antdMessageSuccess("Cost price updated successfully");
      setReFetchDocument(`/api/v1/tr_inner_documents/${documentId}?page=${page}&per_page=${pageCount}`);
    },
    onError: (error) => {
      antdMessageError(error?.response?.data?.message || "Failed to update cost price");
    },
  });

  const updateDepartmentMutation = useMutation({
    mutationFn: ({ itemId, department_code, department_name }) => {
      return Axios.put(`/api/v1/tr_request_items/${itemId}`, {
        tr_request_item: {
          department_code,
          department_name,
        },
      });
    },
    onSuccess: () => {
      antdMessageSuccess("Department updated successfully");
      setReFetchDocument(`/api/v1/tr_inner_documents/${documentId}?page=${page}&per_page=${pageCount}`);
    },
    onError: (error) => {
      antdMessageError(error?.response?.data?.message || "Failed to update department");
    },
  });

  const getDepartmentOptions = () => {
    const stores = valueStores?.stores?.["verslo vartai"] || {};
    const options = Object.entries(stores).map(([key, value]) => {
      const code = key.replace('vv_', '');
      return {
        value: key,
        label: value,
        code: code,
      };
    });

    return [
      {
        value: '',
        label: 'None',
        code: '',
      },
      ...options
    ];
  };

  const handleCreateInternalDocument = () => {
    mutationInner.mutate(documentData?.data?.id);
  };

  const handleCreateInvoice = () => {
    mutationInvoice.mutate(documentData?.data?.id);
  };

  const handleUpdateCost = () => {
    mutationCost.mutate(documentData?.data?.id);
  };

  const handleCreateClientInvoices = () => {
    mutationClientInvoices.mutate(checkedItems);
  };

  const handleSetCanceled = () => {
    mutationCancel.mutate(documentData?.data?.id);
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await Axios.post(`/api/v1/tr_inner_documents/${documentId}/remove_item`, {
        tr_request_item_id: itemId
      });
      antdMessageSuccess("Item deleted successfully");
      setReFetchDocument(`/api/v1/tr_inner_documents/${documentId}?page=${page}&per_page=${pageCount}`);
    } catch (error) {
      antdMessageError(error?.response?.data?.message || "Failed to delete item");
    }
  };

  const orderModal = (order_id, id) => {
    const type = order_id?.startsWith("FO") ? "FakeOrder" : "Order";
    if (type == "FakeOrder") {
      modalRef.current.openModal("FakeOrderShowModal");
      set_order_id(id);
      return;
    }
    set_order_id(order_id);
    dispatch(getOrder(id));
    modalRef.current.openModal("OrderShowModal");
  };

  if (loadingDocument) {
    return (
      <Container
        location={{
          locationName: "Loading...",
          locationUrl: match.url,
        }}
        goBack={() => history.push("/technorama/documents")}
      >
        <ContentBlock>
          <div className="text-center py-5">
            <h4>Loading document...</h4>
          </div>
        </ContentBlock>
      </Container>
    );
  }

  if (!documentData?.data) {
    return (
      <Container
        location={{
          locationName: "Document not found",
          locationUrl: match.url,
        }}
        goBack={() => history.push("/technorama/documents")}
      >
        <ContentBlock>
          <div className="text-center py-5">
            <h4>Document not found</h4>
            <p>The requested document could not be found.</p>
          </div>
        </ContentBlock>
      </Container>
    );
  }

  return (
    <Container
      location={{
        locationName: documentData?.data?.tr_request_id || "Document Details",
        locationUrl: match.url,
      }}
      goBack={() => history.push("/technorama/documents")}
    >
      <>
        <ContentBlock>
          <FilterMenu
            totals={total_items}
            length={documentItems.length}
            onChangeCountPage={setPageCount}
            countPage={pageCount}
            text2="Products"
          >
            <InformationNav documentData={documentData?.data} />
          </FilterMenu>
        </ContentBlock>

        {documentData?.data?.status === 'failed' && documentData?.data?.error && (
          <ContentBlock>
            <ErrorDisplay error={documentData.data.error} />
          </ContentBlock>
        )}

        <ContentBlock>
          <div className="table-responsive">
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <div className="col-12 mb-3 d-flex justify-content-end" style={{ gap: 10 }}>
                {documentData?.data?.status !== 'completed' && !documentData?.data?.inner_document_number && (
                  <div style={{ width: 180 }}>
                    <ButtonSecondary
                      style={{ backgroundColor: "#1F1A54" }}
                      onClick={() => {
                        AntdConfirm({
                          title: "Create Internal Document",
                          content: "Are you sure you want to create an internal document?",
                          onOk: handleCreateInternalDocument,
                        });
                      }}
                    >
                      Create internal document
                    </ButtonSecondary>
                  </div>
                )}

                {documentData?.data?.status !== 'completed' && (
                  <div style={{ width: 180 }}>
                    <ButtonSecondary
                      style={{ backgroundColor: "#03b0f7" }}
                      onClick={() => {
                        AntdConfirm({
                          title: "Update prime cost",
                          content: "Are you sure you want to update prime cost from rivile?",
                          onOk: handleUpdateCost,
                        });
                      }}
                    >
                      Update cost
                    </ButtonSecondary>
                  </div>
                )}

                {documentData?.data?.status !== 'completed' && documentData?.data?.inner_document_number && (
                  <div style={{ width: 180 }}>
                    <ButtonSecondary
                      style={{ backgroundColor: "#1F1A54" }}
                      onClick={() => {
                        AntdConfirm({
                          title: "Create invoice",
                          content: "Are you sure you want to create an invoice?",
                          onOk: handleCreateInvoice,
                        });
                      }}
                    >
                      Create invoice
                    </ButtonSecondary>
                  </div>
                )}

                {canCreateClientInvoices && (
                  <div style={{ width: 210 }}>
                    <ButtonSecondary
                      style={{ backgroundColor: "#1F1A54" }}
                      disabled={checkedItems.length === 0}
                      loading={mutationClientInvoices.isLoading || generatingInvoices}
                      onClick={() => {
                        AntdConfirm({
                          title: "Create client invoices",
                          content: `Invoices will be created for ${checkedItems.length} selected product(s), grouped by order. Continue?`,
                          onOk: handleCreateClientInvoices,
                        });
                      }}
                    >
                      {`Client invoices (${checkedItems.length})`}
                    </ButtonSecondary>
                  </div>
                )}

                {documentData?.data?.status !== "canceled" && documentData?.data?.status !== "completed" && (
                  <div style={{ width: 125 }}>
                    <ButtonSecondary
                      style={{ backgroundColor: "#E61D01" }}
                      onClick={() => {
                        AntdConfirm({
                          title: "Cancel Document",
                          content: "Are you sure you want to cancel this document?",
                          onOk: handleSetCanceled,
                        });
                      }}
                    >
                      Set canceled
                    </ButtonSecondary>
                  </div>
                )}
              </div>
            </div>

            <PaginationComp
              className="mb-0"
              setPagination={setPage}
              page={page}
              totals={total_items}
              pageCount={pageCount}
            />

            <Spin spinning={mutationClientInvoices.isLoading || generatingInvoices} tip="Generating invoices...">
            <TableUi
              ths={documentData?.data?.status === "completed" || documentData?.data?.status === "in_progress" || documentData?.data?.status === "new" ? [
                selectAllTh,
                "Order",
                "BB SKU",
                "VV SKU",
                "Title",
                "Cost price",
                "Stock QTY",
                "Qty",
                "Department",
                "Invoice",
                "Error",
                "",
              ] : 
              [
                selectAllTh,
                "Order",
                "BB SKU",
                "VV SKU",
                "Title",
                "Stock QTY",
                "Qty",
                "Department",
                "Invoice",
                "Error",
                "",
              ]
              }
              total={filteredData?.length || 0}
            >
              {filteredData.flatMap((item) => {
                  // Expand rows based on qty_prime_cost
                  const expandedRows = Object.entries(Object.keys(item.qty_prime_cost || {}).length > 0 ? item.qty_prime_cost : {[item.prime_cost]: item.quantity}).flatMap(
                    ([primeCost, count], index) =>
                      ({
                        ...item,
                        prime_cost: primeCost,
                        quantity: count,
                        uniqueKey: `${item.id}-${primeCost}`,
                        price_index: index,
                      })
                  );

                  return expandedRows;
                }).map((item) => {
                return (
                  <TableColumn
                    key={item.uniqueKey ?? item.id}
                    {...item}
                    onDelete={handleDeleteItem}
                    canDelete={documentData?.data?.status !== "completed" && documentData?.data?.status !== "canceled" && item.price_index == 0}
                    canEditPrice={documentData?.data?.status !== "completed" && documentData?.data?.status !== "canceled" && documentData?.data?.status !== "in_progress"}
                    canShowPrice={documentData?.data?.status === "completed" || documentData?.data?.status === "in_progress" || documentData?.data?.status === "new"}
                    onUpdateCostPrice={updateCostPriceMutation.mutate}
                    isUpdatingCostPrice={updateCostPriceMutation.isLoading}
                    onUpdateDepartment={updateDepartmentMutation.mutate}
                    isUpdatingDepartment={updateDepartmentMutation.isLoading}
                    departmentOptions={getDepartmentOptions()}
                    orderModal={orderModal}
                    canSelect={canCreateClientInvoices && item.price_index == 0 && isInvoiceable(item)}
                    checked={checkedItems.includes(item.id)}
                    onCheckboxChange={handleCheckboxChange}
                  />
                );
              })}
            </TableUi>
            </Spin>

            <PaginationComp
              className="mb-0"
              setPagination={setPage}
              page={page}
              totals={total_items}
              pageCount={pageCount}
            />
          </div>
        </ContentBlock>
      </>
      <div id="modal-roots" />
      <RegularOrderChildren
        ref={modalRef}
        requiredOrderId={order_id}
        modal_type={"TrRequest"}
      />
    </Container>
  );
}

function TableColumn({
  id,
  price_index,
  order_number,
  order_id,
  rivile_code,
  product_name,
  bb_code,
  prime_cost,
  total_cost,
  quantity,
  stock_quantity,
  department_code,
  department_name,
  client_invoice_numbers,
  tr_inner_document_error,
  onDelete,
  canDelete,
  canEditPrice,
  canShowPrice,
  onUpdateCostPrice,
  isUpdatingCostPrice,
  onUpdateDepartment,
  isUpdatingDepartment,
  departmentOptions,
  orderModal,
  canSelect,
  checked,
  onCheckboxChange,
}) {
  return (
    <TableUi.tr className="align-middle tableColumn">
      <TableUi.td className="align-middle">
        {canSelect && (
          <Checkbox
            checked={checked}
            onChange={() => onCheckboxChange(id)}
          />
        )}
      </TableUi.td>
      <TableUi.td className="align-middle">
        <div
          className="text-primary cursor-pointer text-decoration-underline"
          onClick={() => {
            orderModal(order_number, order_id);
          }}
        >
          {order_number}
        </div>
      </TableUi.td>
      <TableUi.td className="align-middle">
        <div style={{ paddingLeft: 10 }}>{bb_code || "-"}</div>
      </TableUi.td>
      <TableUi.td className="align-middle">{rivile_code || "-"}</TableUi.td>
      <TableUi.td className="align-middle">
        <div className="d-flex flex-wrap-wrap">{product_name || "-"}</div>
      </TableUi.td>
      {canShowPrice && <TableUi.td className="align-middle">
        <div className="d-flex align-items-center">
          <div className="mr-2">
            <div className="d-flex flex-row align-items-center">
              {prime_cost ? `${prime_cost} €` : "-"}
              {canEditPrice && (
                <CostPricePopover
                  itemId={id}
                  priceIndex={price_index}
                  onSave={onUpdateCostPrice}
                  currentPrimeCost={prime_cost}
                  isEditing={true}
                />
              )}
            </div>
            <div className="d-flex flex-row align-items-center">
              <Tooltip title="Savikaina +2% (galima koreguoti)">
                <div style={{ backgroundColor: "#E0E0E0", padding: "2px 4px", borderRadius: "18px", marginRight: 4 }}>
                  <p style={{ fontSize: "10px", color: "#00000073", margin: 0, whiteSpace: "nowrap" }}>D +2%</p>
                </div>
              </Tooltip>
              {prime_cost ? `${(prime_cost * 1.02).toFixed(4)} €` : "-"}
            </div>
          </div>
        </div>
      </TableUi.td>}
      <TableUi.td className="align-middle">
        {stock_quantity || 0}
      </TableUi.td>
      <TableUi.td className="align-middle">
        {quantity || 0}
      </TableUi.td>
      <TableUi.td className="align-middle">
        <div className="d-flex align-items-center">
          <span className="mr-2">{department_name + " - " + department_code || "-"}</span>
          {canDelete && (
            <VvDepartmentPopover
              itemId={id}
              onSave={onUpdateDepartment}
              currentDepartmentCode={department_code}
              currentDepartmentName={department_name}
              departmentOptions={departmentOptions}
              isEditing={true}
            />
          )}
        </div>
      </TableUi.td>
      <TableUi.td className="align-middle">
        {client_invoice_numbers?.length ? client_invoice_numbers.join(", ") : "-"}
      </TableUi.td>
      <TableUi.td className="align-middle">
        {tr_inner_document_error ? (
          <div className="text-danger" style={{ fontSize: '12px', maxWidth: '200px', wordWrap: 'break-word' }}>
            {tr_inner_document_error}
          </div>
        ) : (
          "-"
        )}
      </TableUi.td>
      <TableUi.td className="align-middle float-right">
        {canDelete && (
          <button
            className="btn btn-sm btn-danger"
            onClick={() =>
              confirm({
                title: "Are you sure?",
                content: "You will not be able to undo this action",
                onOk() {
                  onDelete(id);
                },
                onCancel() {
                  console.log("Cancel");
                },
              })
            }
          >
            <IconTrash color="#fff" />
          </button>
        )}
      </TableUi.td>
    </TableUi.tr>
  );
}

const ErrorDisplay = ({ error }) => {
  let errorDetails = null;

  try {
    errorDetails = JSON.parse(error);
  } catch (e) {
    errorDetails = { error: error };
  }

  const getErrorMessage = () => {
    if (errorDetails?.error) {
      return `${errorDetails.error}${errorDetails.status ? ` (Status: ${errorDetails.status})` : ''}`;
    }
    return 'An unknown error occurred';
  };

  const getErrorDescription = () => {
    const parts = [];
    if (errorDetails?.timestamp) {
      parts.push(`Time: ${new Date(errorDetails.timestamp).toLocaleString()}`);
    }
    if (errorDetails?.path) {
      parts.push(`Path: ${errorDetails.path}`);
    }
    if (errorDetails?.requestId) {
      parts.push(`Request ID: ${errorDetails.requestId}`);
    }
    return parts.length > 0 ? parts.join(' | ') : null;
  };

  return (
    <Alert
      message="Document Processing Failed"
      description={
        <div>
          <div style={{ marginBottom: 8, fontWeight: 'bold' }}>
            {getErrorMessage()}
          </div>
          {getErrorDescription() && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              {getErrorDescription()}
            </div>
          )}
        </div>
      }
      type="error"
      showIcon
      style={{ marginLeft: "2rem", marginRight: "2rem" }}
    />
  );
};

const InformationNav = ({ documentData }) => {
  return (
    <div className="d-flex align-items-center flex-1 justify-content-between">
      <InformationItem
        icon={<IconNo className="text-primary" fill="#03b0f7" />}
        text={"Sup. order No:"}
        subText={documentData?.supplier_order_number}
      />
      <InformationItem
        icon={<IconWarehouse className="text-primary" fill="#03b0f7" />}
        text={"Department:"}
        subText={`${documentData?.department_code} - ${documentData?.department_name}`}
      />
      <InformationItem
        icon={<IconDocNumber className="text-primary" fill="#03b0f7" />}
        text={"Status:"}
        subText={documentData?.status || 'new'}
      />
      <InformationItem
        icon={<IconWarehouse2 size={20} />}
        text={"Internal doc. no:"}
        subText={documentData?.inner_document_number || "-"}
      />
      <InformationItem
        icon={<MdDateRange className="mr-2 text-primary" size={20} />}
        text={"Invoice no:"}
        subText={documentData?.invoice_number || "-"}
      />
    </div>
  );
};

const InformationItem = ({ icon, text, subText }) => {
  return (
    <span className="mx-3">
      <div className="d-flex">
        <div className="d-flex align-items-center mr-2">{icon}</div>
        <div className="text-sm text-black">
          <div className="font-semi-bold">{text}</div>
          <div
            className="mb-0"
            style={{
              lineHeight: 1.2,
            }}
          >
            {subText || " - "}
          </div>
        </div>
      </div>
    </span>
  );
}; 